/**
 * WEB-694 / O-7 — stored theme preference (`light` | `dark` | `system`).
 *
 * The durable per-device cache is the `loci-theme` cookie (SameSite=Lax, 1 year,
 * not httpOnly) so WEB-693 can read it during SSR. `system` is a real stored
 * value, not the absence of a choice. WEB-693's `parseThemeMode` ignores
 * `system` and SSR-falls back to the brand dark default until the client
 * resolves `prefers-color-scheme`.
 */

import {
  THEME_COOKIE_MAX_AGE_SECONDS,
  THEME_COOKIE_NAME,
  THEME_MIGRATION_STORAGE_KEY,
} from './ssrTheme';

export const THEME_PREFERENCE_COOKIE = THEME_COOKIE_NAME;
export const THEME_PREFERENCE_MAX_AGE_SECONDS = THEME_COOKIE_MAX_AGE_SECONDS;

/** Retired O-7 localStorage key. Read once to seed the cookie, then removed. */
export const LEGACY_PREVIEW_THEME_STORAGE_KEY = THEME_MIGRATION_STORAGE_KEY;

export const THEME_PREFERENCES = ['light', 'dark', 'system'] as const;
export type ThemePreference = (typeof THEME_PREFERENCES)[number];

export type ResolvedThemeMode = 'light' | 'dark';

type ThemePreferenceListener = () => void;

let currentPreference: ThemePreference = 'system';
const listeners = new Set<ThemePreferenceListener>();

function emitThemePreferenceChange(): void {
  listeners.forEach((listener) => listener());
}

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

export function parseThemePreferenceCookie(cookieSource: string): ThemePreference | null {
  const parts = cookieSource.split(';');
  let latest: ThemePreference | null = null;
  for (const part of parts) {
    const trimmed = part.trim();
    const separator = trimmed.indexOf('=');
    if (separator === -1) continue;
    const name = trimmed.slice(0, separator);
    if (name !== THEME_PREFERENCE_COOKIE) continue;
    let value: string;
    try {
      value = decodeURIComponent(trimmed.slice(separator + 1));
    } catch {
      continue;
    }
    if (isThemePreference(value)) {
      // Host-only and Domain=.loci.city copies can coexist. `document.cookie`
      // lists both; the last valid value is the most recent client write
      // (WEB-929). First-wins restored System over a later Dark choice.
      latest = value;
    }
  }
  return latest;
}

export function readThemePreferenceCookie(): ThemePreference | null {
  if (typeof document === 'undefined') return null;
  return parseThemePreferenceCookie(document.cookie);
}

export function writeThemePreferenceCookie(preference: ThemePreference): void {
  if (typeof document === 'undefined') return;
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const attributes = [
    `${THEME_PREFERENCE_COOKIE}=${encodeURIComponent(preference)}`,
    'Path=/',
    `Max-Age=${THEME_PREFERENCE_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
  ];
  if (secure) {
    attributes.push('Secure');
  }
  document.cookie = attributes.join('; ');
}

export function getSystemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    // O-7 brand default is dark when the OS preference cannot be read.
    return true;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveThemeMode(
  preference: ThemePreference,
  prefersDark: boolean,
): ResolvedThemeMode {
  switch (preference) {
    case 'light':
      return 'light';
    case 'dark':
      return 'dark';
    case 'system':
      return prefersDark ? 'dark' : 'light';
    default: {
      const _exhaustive: never = preference;
      return _exhaustive;
    }
  }
}

export function getThemePreference(): ThemePreference {
  return currentPreference;
}

/**
 * Client snapshot for `useSyncExternalStore`. The cookie is the durable
 * per-device cache; the in-memory store is a fallback when no cookie exists
 * yet (first paint, or backend-cleared `system`).
 */
export function getThemePreferenceSnapshot(): ThemePreference {
  return readThemePreferenceCookie() ?? currentPreference;
}

export function subscribeThemePreference(listener: ThemePreferenceListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * One-release migration: copy a leftover `loci-preview-theme` localStorage
 * value into the cookie when no cookie exists, then drop the legacy key.
 * ThemeProvider previously stored only the resolved `light` | `dark` mode.
 */
export function migrateLegacyThemePreference(): ThemePreference | null {
  if (typeof window === 'undefined') return null;
  try {
    const legacy = window.localStorage.getItem(LEGACY_PREVIEW_THEME_STORAGE_KEY);
    if (!legacy) return null;
    const existing = readThemePreferenceCookie();
    if (!existing && (legacy === 'light' || legacy === 'dark')) {
      writeThemePreferenceCookie(legacy);
    }
    window.localStorage.removeItem(LEGACY_PREVIEW_THEME_STORAGE_KEY);
    return existing ?? (legacy === 'light' || legacy === 'dark' ? legacy : null);
  } catch {
    return null;
  }
}

export function hydrateThemePreferenceFromCookie(): ThemePreference {
  migrateLegacyThemePreference();
  const stored = readThemePreferenceCookie();
  if (stored && stored !== currentPreference) {
    currentPreference = stored;
    emitThemePreferenceChange();
  }
  return currentPreference;
}

/**
 * Cross-device fallback: apply the account field only when this device has
 * no `loci-theme` cookie yet. A present cookie is the per-device source of
 * truth and must not be clobbered by a stale `/auth/me` payload (e.g. the
 * user already changed the control before bootstrap finished).
 */
export function applyAccountThemePreference(accountTheme: unknown): void {
  if (!isThemePreference(accountTheme)) return;
  if (readThemePreferenceCookie()) return;
  // Cookie can be cleared by a later Set-Cookie (account still `system`)
  // after this device already chose Light/Dark. Do not clobber that choice.
  if (currentPreference !== 'system') return;
  setStoredThemePreference(accountTheme);
}

export function setStoredThemePreference(preference: ThemePreference): void {
  writeThemePreferenceCookie(preference);
  if (currentPreference === preference) return;
  currentPreference = preference;
  emitThemePreferenceChange();
}

/** Test-only: reset the module store between specs. */
export function resetThemePreferenceStoreForTests(preference: ThemePreference = 'system'): void {
  listeners.clear();
  currentPreference = preference;
}
